import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { classNames } from "../../utils/helpers";
import { View } from "../view";
import { Button } from "../button";
import { Link } from "../link";
import { Icon } from "../icon";
import { Actionable } from "../actionable";
import { CheckmarkCircle, InfoCircle, ExclamationCircle, ExclamationTriangle, Close, } from '@az/starc-ui-icons';
import { Text } from "../text";
import s from './Notification.module.css';
export const Notification = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    const { type, text, children, title, hasCloseButton, onClose, className, closeButtonTitle = 'Close', attributes, isToast, ctaLabel, ctaType = 'button', ctaOnClick, customNotification, } = props;
    const notificationClassNames = classNames(s.notification, type && s[`--type-${type}`], !isToast && [s['--inline'], s[`--border-${type}`]]);
    const hasCta = ctaLabel && ctaOnClick;
    const VARIANT = {
        info: {
            svg: InfoCircle,
            iconColor: 'focus',
            color: undefined,
            style: 'default',
            bgColor: 'background',
            borderRadius: undefined,
            barColor: 'focus',
            closeColor: undefined,
        },
        success: {
            svg: CheckmarkCircle,
            iconColor: 'success',
            color: undefined,
            style: 'default',
            bgColor: 'background',
            borderRadius: undefined,
            barColor: 'success',
            closeColor: undefined,
        },
        error: {
            svg: ExclamationCircle,
            iconColor: 'error',
            color: undefined,
            style: 'default',
            bgColor: 'background',
            borderRadius: undefined,
            barColor: 'error',
            closeColor: undefined,
        },
        warning: {
            svg: ExclamationTriangle,
            iconColor: 'warning',
            color: undefined,
            style: 'default',
            bgColor: 'background',
            borderRadius: undefined,
            barColor: 'warning',
            closeColor: undefined,
        },
        'high-alert': {
            svg: ExclamationTriangle,
            iconColor: 'warning',
            color: 'on-primary',
            style: 'default',
            bgColor: '500',
            borderRadius: undefined,
            barColor: undefined,
            closeColor: undefined,
        },
        'snack-bar': {
            svg: undefined,
            iconColor: undefined,
            color: 'on-primary',
            style: 'default',
            bgColor: '500',
            borderRadius: 'small',
            barColor: undefined,
            closeColor: 'on-primary',
        },
        custom: customNotification,
    };
    const inlineProps = {
        direction: 'row',
        gap: 2,
        align: 'baseline',
    };
    const textContent = (title || text) && (_jsxs(View, Object.assign({}, (((_a = VARIANT[type]) === null || _a === void 0 ? void 0 : _a.style) === 'inline' ? Object.assign({}, inlineProps) : null), { children: [title && (_jsx(Text, { variant: "main-body", weight: "bold", color: (_b = VARIANT[type]) === null || _b === void 0 ? void 0 : _b.color, children: title })), _jsx(Text, { variant: "small-body", color: (_c = VARIANT[type]) === null || _c === void 0 ? void 0 : _c.color, children: text })] })));
    const icon = type in VARIANT ? (_d = VARIANT[type]) === null || _d === void 0 ? void 0 : _d.svg : undefined;
    const rootClassName = classNames(className, notificationClassNames);
    const notificationNode = (_jsx(View, { className: rootClassName, borderRadius: (_e = VARIANT[type]) === null || _e === void 0 ? void 0 : _e.borderRadius, attributes: Object.assign(Object.assign({ 'data-testid': 'st-notification', role: 'alert' }, attributes), { style: {
                border: !isToast ? `1px solid var(--st-notification-bar-color)` : undefined,
                '--st-notification-color': type
                    ? `var(--st-color-${(_f = VARIANT[type]) === null || _f === void 0 ? void 0 : _f.bgColor})`
                    : 'var(--st-color-background)',
                '--st-notification-bar-color': type
                    ? `var(--st-color-${(_g = VARIANT[type]) === null || _g === void 0 ? void 0 : _g.barColor})`
                    : 'var(--st-color-background)',
            } }), children: _jsxs(View, { direction: "row", gap: 5, align: "center", children: [icon && _jsx(Icon, { size: 5, svg: icon, color: (_h = VARIANT[type]) === null || _h === void 0 ? void 0 : _h.iconColor }), _jsx(View.Item, { grow: true, children: _jsxs(View, { direction: "row", gap: 5, align: "center", children: [_jsx(View.Item, { grow: true, children: (textContent && children) || type !== 'snack-bar' ? (_jsxs(View, { gap: 1, children: [textContent, children && _jsx(View.Item, { gapBefore: 3, children: children })] })) : (textContent || children) }), _jsxs(View, { direction: "row", align: "center", gap: 2, children: [typeof ctaLabel === 'string' &&
                                        hasCta &&
                                        (ctaType === 'button' ? (_jsx(Button, { variant: "ghost", size: "small", className: classNames(s['notification-button'], s['--button']), fontColor: (_j = VARIANT[type]) === null || _j === void 0 ? void 0 : _j.color, attributes: { 'aria-label': ctaLabel }, onClick: ctaOnClick, children: ctaLabel })) : (_jsx(Link, { onClick: ctaOnClick, attributes: { color: `${(_k = VARIANT[type]) === null || _k === void 0 ? void 0 : _k.color}` }, children: ctaLabel }))), typeof ctaLabel != 'string' && hasCta && (_jsx(Actionable, { onClick: ctaOnClick, children: _jsx(View, { padding: [0, 5], children: ctaLabel }) })), hasCloseButton && (_jsx(Button, { variant: "ghost", size: "small", className: classNames(s['notification-button'], s['--button']), onClick: onClose, attributes: { 'aria-label': closeButtonTitle }, children: _jsx(Icon, { size: 6, svg: _jsx(Close, {}), color: (_l = VARIANT[type]) === null || _l === void 0 ? void 0 : _l.closeColor }) }))] })] }) })] }) }));
    return notificationNode;
};
